'use client';

import { useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import type { NextPage } from 'next';
import { useUser } from '@context/user-context';
import WithUser from '@components/WithUser/WithUser';
import { useCheckout } from '@hooks/billing-subscription';

const PREMIUM_UPGRADE_SESSION_KEY = 'premiumUpgradeSession';

// Changing these types will require changes in webflow -
type WebFlowIntervalQuery = 'month' | 'year';
type WebFlowPlanQuery = 'premium' | 'business';

function getSelectedPlanFromQuery(query: string | string[] | undefined): WebFlowPlanQuery | undefined {
  if (query === 'premium') return 'premium';
  if (query === 'business') return 'business';
  return;
}

function getSelectedIntervalFromQuery(query: string | string[] | undefined): WebFlowIntervalQuery {
  if (query === 'month') return 'month';
  if (query === 'year') return 'year';
  return 'year';
}

const UpgradeFlowSelectionPage: NextPage = () => {
  const { user, loading } = useUser();
  const { replace, isReady, query } = useRouter();
  const { createUpgradeSession } = usePremiumUpgradeFlow();

  useEffect(() => {
    if (loading || !isReady) return;

    // Check if window is available
    if (typeof window !== 'undefined') {
      const isMobile = window.matchMedia('(max-width: 700px)');
      if (isMobile.matches) {
        const queryParams = new URLSearchParams(query as Record<string, string>).toString();
        replace(`/onboarding/personal-details?${queryParams}`);
        return;
      }
    }

    const selectedPlan = getSelectedPlanFromQuery(query.plan);

    // Business to business v2 upgrade flow
    if (selectedPlan === 'business') {
      replace('/onboarding/business/v2/signup');
      return;
    }

    const interval = getSelectedIntervalFromQuery(query.interval);
    createUpgradeSession({ interval });

    // If logged in
    if (user) {
      const isTeamAdmin = !!user.bbOrgId;
      const currentWorkspace = user.bbOrgId ?? user.userId;

      // TODO: Future optimisation, hoise home-page logic, this is not DRY
      // If user is logged in, we want to show them a dialog on their home page
      if (isTeamAdmin) {
        replace({
          pathname: `/${currentWorkspace}/home`,
        });
      } else {
        replace({
          pathname: `/${currentWorkspace}/cards/personal`,
        });
      }
    } else {
      // If the user is logged out, we need to send them to the onboarding flow.
      replace('/onboarding/customize-card');
      return;
    }
  }, [loading, user, isReady, createUpgradeSession, replace, query]);

  return <></>;
};

export default WithUser(UpgradeFlowSelectionPage, false);

const UPGRADE_SUCCESS_QUERY = 'upgradeSuccess';

export const usePremiumUpgradeFlow = (): {
  isInUpgradeSession: boolean;
  createUpgradeSession: (args: { interval: WebFlowIntervalQuery }) => void;
  endUpgradeSession: () => void;
  startUpgradeCheckout: (args: { userId: string; successUrl?: string; cancelUrl?: string }) => void;
} => {
  const router = useRouter();
  const { startCheckout } = useCheckout();
  const [isInUpgradeSession, setIsInUpgradeSession] = useState(
    typeof window !== 'undefined' && Boolean(window.sessionStorage.getItem(PREMIUM_UPGRADE_SESSION_KEY))
  );

  const hasCompletedFlow = router.query[UPGRADE_SUCCESS_QUERY] === 'true';

  useEffect(() => {
    if (hasCompletedFlow) {
      endUpgradeSession();
    }
  }, [hasCompletedFlow]);

  const createUpgradeSession = ({ interval }: { interval: WebFlowIntervalQuery }) => {
    setIsInUpgradeSession(true);
    window?.sessionStorage.setItem(
      PREMIUM_UPGRADE_SESSION_KEY,
      JSON.stringify({
        interval,
      })
    );
  };

  const endUpgradeSession = () => {
    setIsInUpgradeSession(false);
    window?.sessionStorage.removeItem(PREMIUM_UPGRADE_SESSION_KEY);
  };

  const startUpgradeCheckout = ({
    userId,
    successUrl,
    cancelUrl,
  }: {
    userId: string;
    successUrl?: string;
    cancelUrl?: string;
  }) => {
    const session = window?.sessionStorage.getItem(PREMIUM_UPGRADE_SESSION_KEY);
    const interval = session ? JSON.parse(session).interval : 'year';

    if (session && interval) {
      window.sessionStorage.setItem(
        PREMIUM_UPGRADE_SESSION_KEY,
        JSON.stringify({
          interval,
        })
      );

      startCheckout({
        workspaceId: userId,
        interval: interval,
        type: 'INDIVIDUAL',
        successUrl: `${successUrl || window.location.href}?${UPGRADE_SUCCESS_QUERY}=true`,
        cancelUrl,
      });
    }
  };

  return {
    isInUpgradeSession,
    createUpgradeSession,
    endUpgradeSession,
    startUpgradeCheckout,
  };
};
